const isLocalAdmin = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const DEFAULT_API_BASE = isLocalAdmin ? 'https://api.thepockethost.com' : '';
const BASE = process.env.REACT_APP_API_URL || DEFAULT_API_BASE;
const DEFAULT_TIMEOUT_MS = 20000;
const MAX_CONCURRENT_REQUESTS = 3;
const ADMIN_METADATA_CACHE_MS = 5 * 60 * 1000;
const ADMIN_SHORT_CACHE_MS = 15 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const UPSTREAM_ERROR_STATUSES = [502, 503, 504];

export function isHtmlErrorBody(text = '') {
  const value = String(text || '').trim().toLowerCase();
  return value.startsWith('<!doctype html')
    || value.startsWith('<html')
    || value.includes('<title>thepockethost.com | 502: bad gateway</title>')
    || value.includes('cloudflare ray id');
}

function withRequestId(message, requestId) {
  return requestId ? `${message} (request ${requestId})` : message;
}

export function formatApiErrorMessage({ status, statusText, path, text, detail, requestId }) {
  if (typeof detail === 'string' && detail.trim()) return withRequestId(detail, requestId);
  if (detail?.message) return withRequestId(detail.message, requestId);
  if (UPSTREAM_ERROR_STATUSES.includes(status) && isHtmlErrorBody(text)) {
    return withRequestId(`Admin API upstream returned ${status} while loading ${path}. Try again in a moment.`, requestId);
  }
  if (isHtmlErrorBody(text)) {
    return withRequestId(`Admin API returned an HTML error page while loading ${path}. Try again in a moment.`, requestId);
  }
  return withRequestId(text || statusText || 'Request failed', requestId);
}

class Api {
  constructor() {
    this.password = sessionStorage.getItem('mph_admin_pw') || '';
    this.activeRequests = 0;
    this.requestQueue = [];
    this.responseCache = new Map();
  }

  get _auth() {
    return 'Basic ' + btoa('admin:' + this.password);
  }

  setPassword(pw) {
    this.password = pw;
    sessionStorage.setItem('mph_admin_pw', pw);
    this.clearResponseCache();
  }

  clearPassword() {
    this.password = '';
    sessionStorage.removeItem('mph_admin_pw');
    this.clearResponseCache();
  }

  clearResponseCache(prefix = '') {
    if (!prefix) {
      this.responseCache.clear();
      return;
    }
    Array.from(this.responseCache.keys()).forEach(key => {
      if (key.startsWith(prefix)) this.responseCache.delete(key);
    });
  }

  async _withRequestSlot(fn) {
    if (this.activeRequests >= MAX_CONCURRENT_REQUESTS) {
      await new Promise(resolve => this.requestQueue.push(resolve));
    }
    this.activeRequests += 1;
    try {
      return await fn();
    } finally {
      this.activeRequests = Math.max(this.activeRequests - 1, 0);
      const next = this.requestQueue.shift();
      if (next) next();
    }
  }

  async _fetch(path, opts = {}) {
    return this._withRequestSlot(() => this._fetchNow(path, opts));
  }

  async _cachedFetch(cacheKey, path, opts = {}) {
    const { ttlMs = ADMIN_METADATA_CACHE_MS, ...fetchOpts } = opts;
    const now = Date.now();
    const cached = this.responseCache.get(cacheKey);
    if (cached && cached.expiresAt > now) {
      if (cached.promise) return cached.promise;
      return cached.value;
    }
    const promise = this._fetch(path, fetchOpts)
      .then(value => {
        this.responseCache.set(cacheKey, {
          value,
          expiresAt: Date.now() + ttlMs,
        });
        return value;
      })
      .catch(error => {
        this.responseCache.delete(cacheKey);
        throw error;
      });
    this.responseCache.set(cacheKey, {
      promise,
      expiresAt: now + ttlMs,
    });
    return promise;
  }

  async _fetchNow(path, opts = {}) {
    const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOpts } = opts;
    const method = (opts.method || 'GET').toUpperCase();
    const canRetry = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    const attempts = canRetry ? 3 : 1;
    let res = null;
    let lastNetworkError = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const headers = { Authorization: this._auth, ...fetchOpts.headers };
        if (fetchOpts.body && !headers['Content-Type']) {
          headers['Content-Type'] = 'application/json';
        }
        res = await fetch(BASE + path, {
          ...fetchOpts,
          signal: controller.signal,
          headers,
        });
      } catch (error) {
        const requestError = error.name === 'AbortError'
          ? new Error(`${method} ${path} timed out after ${timeoutMs}ms`)
          : error;
        lastNetworkError = requestError;
        if (!canRetry || attempt === attempts - 1) throw requestError;
        await sleep(500 * (attempt + 1));
        continue;
      } finally {
        clearTimeout(timeout);
      }
      if (!canRetry || !UPSTREAM_ERROR_STATUSES.includes(res.status) || attempt === attempts - 1) {
        break;
      }
      await sleep(500 * (attempt + 1));
    }
    if (!res && lastNetworkError) throw lastNetworkError;
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      const requestId = res.headers.get('x-request-id') || '';
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch (_e) {
        payload = null;
      }
      const detail = payload?.detail;
      const message = formatApiErrorMessage({
        status: res.status,
        statusText: res.statusText,
        path,
        text,
        detail,
        requestId: payload?.request_id || requestId,
      });
      const trimmedMessage = String(message || '').length > 500
        ? `${String(message).slice(0, 500)}...`
        : message;
      const error = new Error(`${res.status}: ${trimmedMessage}`);
      error.status = res.status;
      error.payload = payload;
      error.detail = detail;
      error.requestId = payload?.request_id || requestId;
      throw error;
    }
    if (res.status === 204) return null;
    return res.json();
  }

  // Auth check
  checkAuth() { return this._fetch('/api/admin/auth-check', { timeoutMs: 5000 }); }
  getAdminCapabilities() {
    return this._cachedFetch('capabilities', '/api/admin/capabilities', { ttlMs: ADMIN_METADATA_CACHE_MS });
  }
  getAgentWorkbenchOverview(params = {}) {
    const q = new URLSearchParams({ run_limit: '25', safe: 'true', ...params }).toString();
    return this._cachedFetch(`agent-workbench-overview:${q}`, `/api/admin/agent-workbench/overview?${q}`, {
      ttlMs: ADMIN_SHORT_CACHE_MS,
    });
  }
  getAgentWorkbenchSection(section, params = {}) {
    const q = new URLSearchParams({ run_limit: '10', ...params }).toString();
    return this._fetch(`/api/admin/agent-workbench/sections/${section}?${q}`, { timeoutMs: 8000 });
  }
  retryAgentWorkbenchRun(runTable, runId, reason = '') {
    return this._fetch(`/api/admin/agent-workbench/runs/${runTable}/${runId}/retry`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
  updateSourceIntelligenceConfig(body = {}) {
    this.clearResponseCache('agent-workbench-overview:');
    this.clearResponseCache('capabilities');
    return this._fetch('/api/admin/agent-workbench/scraper/source-intelligence', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  startCardEnrichmentEvalPack(body = {}) {
    return this._fetch('/api/admin/agent-workbench/enrichment/eval-pack', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  seedCardEnrichmentEvalSources(body = {}) {
    return this._fetch('/api/admin/agent-workbench/enrichment/eval-source-seed', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  startCardEnrichmentCandidateEval(body = {}) {
    return this._fetch('/api/admin/agent-workbench/enrichment/candidate-eval', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  runCardEnrichmentCandidateEvalWithKey(body = {}) {
    return this._fetch('/api/admin/agent-workbench/enrichment/candidate-eval/manual-key', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  startTaxonomySemanticEvalPack(body = {}) {
    return this._fetch('/api/admin/agent-workbench/librarian/semantic-eval-pack', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  startTaxonomySemanticCandidateEval(body = {}) {
    return this._fetch('/api/admin/agent-workbench/librarian/semantic-candidate-eval', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  startWarningRegistryCheck() {
    return this._fetch('/api/admin/agent-workbench/warnings/registry-check', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }
  startEnrichmentAttributeProseAudit(body = {}) {
    return this._fetch('/api/admin/agent-workbench/enrichment/attribute-prose-audit', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // Guest leads
  getLeads() { return this._fetch('/api/admin/guest-leads'); }
  patchLead(id, body) { return this._fetch(`/api/admin/guest-leads/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); }
  deleteLead(id) { return this._fetch(`/api/admin/guest-leads/${id}`, { method: 'DELETE' }); }
  runPipeline(id) { return this._fetch(`/api/admin/guest-leads/${id}/run-pipeline`, { method: 'POST' }); }
  composerPreview(id) { return this._fetch(`/api/admin/guest-leads/${id}/composer-preview`); }
  sendAbandonedFollowups() { return this._fetch('/api/admin/guest-leads/send-abandoned-followups', { method: 'POST' }); }

  // Composer
  getComposerCorpus(params = {}) {
    const q = new URLSearchParams(params).toString();
    return this._fetch(`/api/admin/composer/corpus${q ? `?${q}` : ''}`, { timeoutMs: 30000 });
  }
  runComposerPreview(body = {}) {
    return this._fetch('/api/admin/composer/preview', {
      method: 'POST',
      timeoutMs: 30000,
      body: JSON.stringify(body),
    });
  }

  // Business intakes
  getBusinesses() { return this._fetch('/api/admin/business-intakes'); }
  patchBusiness(id, body) { return this._fetch(`/api/admin/business-intakes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); }

  // Place cards
  getPlaceCards(params = {}) {
    const q = new URLSearchParams({ limit: '100', ...params }).toString();
    return this._fetch(`/api/admin/place-cards?${q}`);
  }
  getPlaceCardStageCounts(params = {}) {
    const q = new URLSearchParams(params).toString();
    const path = `/api/admin/place-cards/stage-counts${q ? `?${q}` : ''}`;
    return this._cachedFetch(`place-card-stage-counts:${q}`, path, { ttlMs: ADMIN_SHORT_CACHE_MS });
  }
  getLaunchBucketTriage(params = {}) {
    const q = new URLSearchParams({ area: 'Bracebridge', ...params }).toString();
    return this._fetch(`/api/admin/place-cards/launch-triage?${q}`, { timeoutMs: 30000 });
  }
  getPlaceCardDenormalizedConflicts(params = {}) {
    const q = new URLSearchParams({ limit: '500', ...params }).toString();
    return this._fetch(`/api/admin/place-cards/denormalized-conflicts?${q}`);
  }
  repairPlaceCardDenormalizedConflicts(params = {}) {
    const q = new URLSearchParams({ limit: '500', ...params }).toString();
    return this._fetch(`/api/admin/place-cards/denormalized-conflicts/repair?${q}`, { method: 'POST' });
  }
  createPlaceCard(body) { return this._fetch('/api/admin/place-cards', { method: 'POST', body: JSON.stringify(body) }); }
  _cardQuery(cardKey) {
    const key = cardKey || 'primary';
    return `?${new URLSearchParams({ card_key: key }).toString()}`;
  }
  patchPlaceCard(id, body, cardKey = 'primary') {
    return this._fetch(`/api/admin/place-cards/${id}${this._cardQuery(cardKey)}`, { method: 'PATCH', body: JSON.stringify(body) });
  }
  getPlaceCard(id, cardKey = 'primary') { return this._fetch(`/api/admin/place-cards/${id}${this._cardQuery(cardKey)}`); }
  getPlace(id) { return this._fetch(`/api/admin/places/${id}`); }
  patchPlace(id, body) { return this._fetch(`/api/admin/places/${id}`, { method: 'PATCH', body: JSON.stringify(body) }); }
  createPlaceContact(id, body) { return this._fetch(`/api/admin/places/${id}/contacts`, { method: 'POST', body: JSON.stringify(body) }); }
  patchPlaceContact(id, contactId, body) { return this._fetch(`/api/admin/places/${id}/contacts/${contactId}`, { method: 'PATCH', body: JSON.stringify(body) }); }
  deletePlaceContact(id, contactId) { return this._fetch(`/api/admin/places/${id}/contacts/${contactId}`, { method: 'DELETE' }); }
  createPlaceSource(id, body) { return this._fetch(`/api/admin/places/${id}/sources`, { method: 'POST', body: JSON.stringify(body) }); }
  patchPlaceSource(id, sourceId, body) { return this._fetch(`/api/admin/places/${id}/sources/${sourceId}`, { method: 'PATCH', body: JSON.stringify(body) }); }
  deletePlaceSource(id, sourceId) { return this._fetch(`/api/admin/places/${id}/sources/${sourceId}`, { method: 'DELETE' }); }
  createManualSourceSnapshot(id, cardKey, body) {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/manual-source-snapshots`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  getWorkbenchCard(id, cardKey) { return this._fetch(`/api/admin/places/${id}/cards/${cardKey}`); }
  patchWorkbenchCard(id, cardKey, body) {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}`, { method: 'PATCH', body: JSON.stringify(body) });
  }
  archiveWorkbenchCard(id, cardKey, reason = '') {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/archive`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  restoreWorkbenchCard(id, cardKey) {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/restore`, { method: 'POST' });
  }
  deleteWorkbenchCard(id, cardKey) {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}`, { method: 'DELETE' });
  }
  approvePlaceCard(id, reason = '', cardKey = 'primary') {
    return this._fetch(`/api/admin/place-cards/${id}/approve${this._cardQuery(cardKey)}`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  publishPlaceCard(id, reason = '', cardKey = 'primary') {
    return this._fetch(`/api/admin/place-cards/${id}/publish${this._cardQuery(cardKey)}`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  approveAndPublishPlaceCard(id, reason = '', cardKey = 'primary') {
    return this._fetch(`/api/admin/place-cards/${id}/approve-and-publish${this._cardQuery(cardKey)}`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  approveWorkbenchCard(id, cardKey, reason = '') {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/approve`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  publishWorkbenchCard(id, cardKey, reason = '') {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/publish`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  runWorkbenchCardCommand(id, cardKey, command, reason = '') {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/commands/${command}`, { method: 'POST', body: JSON.stringify({ reason }) });
  }
  acceptUnknownComposerSignal(id, cardKey, dimension, reason = '') {
    return this._fetch(`/api/admin/places/${id}/cards/${cardKey}/composer-signals/accept-unknown`, {
      method: 'POST',
      body: JSON.stringify({ dimension, reason }),
    });
  }
  getCardsForPlace(id) { return this._fetch(`/api/admin/place-cards/${id}/cards`); }
  getWorkbenchCardsForPlace(id) { return this._fetch(`/api/admin/places/${id}/cards`); }
  addCardForPlace(id, body) { return this._fetch(`/api/admin/place-cards/${id}/cards`, { method: 'POST', body: JSON.stringify(body) }); }
  addWorkbenchCardForPlace(id, body) {
    // Use the long-standing create alias; keyed Workbench reads/edits still use /places/{id}/cards/{card_key}.
    return this._fetch(`/api/admin/place-cards/${id}/cards`, { method: 'POST', body: JSON.stringify(body) });
  }

  // Change requests
  getChangeRequestCounts() {
    return this._cachedFetch('change-request-counts', '/api/admin/change-requests/counts', {
      timeoutMs: 5000,
      ttlMs: ADMIN_SHORT_CACHE_MS,
    });
  }
  getProposalItems(params = {}) {
    const q = new URLSearchParams({ limit: '100', ...params }).toString();
    return this._fetch(`/api/admin/proposal-items?${q}`);
  }
  getChangeRequests(params = {}) {
    const q = new URLSearchParams({ limit: '100', ...params }).toString();
    return this._fetch(`/api/admin/change-requests?${q}`);
  }
  async patchChangeRequest(id, body) {
    const result = await this._fetch(`/api/admin/change-requests/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    this.clearResponseCache('change-request-counts');
    return result;
  }
  reviewChangeRequestField(requestId, fieldId, body) {
    return this._fetch(`/api/admin/change-requests/${requestId}/fields/${fieldId}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(result => {
        this.clearResponseCache('change-request-counts');
        return result;
      });
  }
  reviewSignalProposal(signalId, body) {
    return this._fetch(`/api/admin/place-signal-proposals/${signalId}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(result => {
        this.clearResponseCache('change-request-counts');
        return result;
      });
  }
  reviewCardAttributeProposal(attributeId, body) {
    return this._fetch(`/api/admin/card-attribute-proposals/${attributeId}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(result => {
        this.clearResponseCache('change-request-counts');
        return result;
      });
  }
  reviewWarningProposal(placeMasterId, cardKey, scope, warningCode, body) {
    return this._fetch(`/api/admin/places/${placeMasterId}/cards/${cardKey}/warnings/${scope}/${warningCode}`, { method: 'PATCH', body: JSON.stringify(body) })
      .then(result => {
        this.clearResponseCache('change-request-counts');
        return result;
      });
  }
  createListingFromChangeRequest(id, body = {}) {
    return this._fetch(`/api/admin/change-requests/${id}/create-listing`, { method: 'POST', body: JSON.stringify(body) });
  }
  createCardFromChangeRequest(id, body = {}) {
    return this._fetch(`/api/admin/change-requests/${id}/create-card`, { method: 'POST', body: JSON.stringify(body) });
  }
  mergeCardCreateChangeRequest(id, body) {
    return this._fetch(`/api/admin/change-requests/${id}/merge-card`, { method: 'POST', body: JSON.stringify(body) });
  }
  mergeChangeRequest(id, body) {
    return this._fetch(`/api/admin/change-requests/${id}/merge`, { method: 'POST', body: JSON.stringify(body) });
  }

  // Card tags
  getTags() { return this._cachedFetch('use-case-tags', '/api/admin/use-case-tags', { ttlMs: ADMIN_METADATA_CACHE_MS }); }
  async createTag(body) {
    const result = await this._fetch('/api/admin/use-case-tags', { method: 'POST', body: JSON.stringify(body) });
    this.clearResponseCache('use-case-tags');
    return result;
  }
  async updateTag(id, body) {
    const result = await this._fetch(`/api/admin/use-case-tags/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    this.clearResponseCache('use-case-tags');
    return result;
  }
  async deleteTag(id) {
    const result = await this._fetch(`/api/admin/use-case-tags/${id}`, { method: 'DELETE' });
    this.clearResponseCache('use-case-tags');
    return result;
  }

  // Vocabulary terms (sector, category_type, etc.)
  getVocabularyTerms(family, status = null, options = {}) {
    const params = new URLSearchParams({ family });
    if (status) params.set('status', status);
    if (options.includeUsage) params.set('include_usage', 'true');
    const path = `/api/admin/vocabulary-terms?${params}`;
    return this._cachedFetch(`vocabulary-terms:${params}`, path, { ttlMs: ADMIN_METADATA_CACHE_MS });
  }
  createVocabularyTerm(family, label, extra = {}) {
    return this._fetch('/api/admin/vocabulary-terms', {
      method: 'POST',
      body: JSON.stringify({ family, value: label, label, status: 'pending', ...extra }),
    }).then(result => {
      this.clearResponseCache('vocabulary-terms:');
      return result;
    });
  }
  async updateVocabularyTerm(id, body) {
    const result = await this._fetch(`/api/admin/vocabulary-terms/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    this.clearResponseCache('vocabulary-terms:');
    return result;
  }
  mergeVocabularyTerm(id, target_term_id, reason = '') {
    return this._fetch(`/api/admin/vocabulary-terms/${id}/merge`, { method: 'POST', body: JSON.stringify({ target_term_id, reason }) })
      .then(result => {
        this.clearResponseCache('vocabulary-terms:');
        return result;
      });
  }
  getVocabularyFacets(family, status = null, options = {}) {
    const params = new URLSearchParams({ family });
    if (status) params.set('status', status);
    if (options.includeUsage) params.set('include_usage', 'true');
    const path = `/api/admin/vocabulary-facets?${params}`;
    return this._cachedFetch(`vocabulary-facets:${params}`, path, { ttlMs: ADMIN_METADATA_CACHE_MS });
  }
  createVocabularyFacet(family, label, extra = {}) {
    return this._fetch('/api/admin/vocabulary-facets', {
      method: 'POST',
      body: JSON.stringify({ family, value: label, label, status: 'pending', ...extra }),
    }).then(result => {
      this.clearResponseCache('vocabulary-facets:');
      return result;
    });
  }
  async updateVocabularyFacet(id, body) {
    const result = await this._fetch(`/api/admin/vocabulary-facets/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
    this.clearResponseCache('vocabulary-facets:');
    return result;
  }
  rewriteTaxonomyValue(body) {
    return this._fetch('/api/admin/taxonomy/librarian/rewrite-value', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  migrateTaxonomyValueToRetrievalLane(body) {
    return this._fetch('/api/admin/taxonomy/librarian/migrate-to-retrieval-lane', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  getTaxonomyServicesSplitCandidates(params = {}) {
    const q = new URLSearchParams({ limit: '100', ...params }).toString();
    return this._fetch(`/api/admin/taxonomy/librarian/services-split-candidates?${q}`);
  }
  classifyTaxonomyServicesSplitCard(body) {
    return this._fetch('/api/admin/taxonomy/librarian/services-split-classify', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  normalizeLegacyTaxonomyBuckets(reason = '') {
    return this._fetch('/api/admin/taxonomy/librarian/normalize-legacy-buckets', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }
  ignoreTaxonomyLibrarianProposal(body) {
    return this._fetch('/api/admin/taxonomy/librarian/ignore-proposal', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  deleteVocabularyTerm(id) { return this._fetch(`/api/admin/vocabulary-terms/${id}`, { method: 'DELETE' }); }

  // Reusable admin jobs and live taxonomy librarian reporting
  startTaxonomyLibrarianRun() {
    return this._fetch('/api/admin/taxonomy/librarian/runs', { method: 'POST' });
  }
  getLatestTaxonomyLibrarianRun() {
    return this._fetch('/api/admin/taxonomy/librarian/runs/latest');
  }
  startCardTagCleanupRun() {
    return this._fetch('/api/admin/taxonomy/card-tag-cleanup/runs', { method: 'POST' });
  }
  getLatestCardTagCleanupRun() {
    return this._fetch('/api/admin/taxonomy/card-tag-cleanup/runs/latest');
  }
  removeCardTagCleanupTag(body) {
    return this._fetch('/api/admin/taxonomy/card-tag-cleanup/remove-tag', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  rejectCardTagCleanupAlias(body) {
    return this._fetch('/api/admin/taxonomy/card-tag-cleanup/reject-alias', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  getAdminJob(id) { return this._fetch(`/api/admin/jobs/${id}`); }
  async streamAdminJob(id, onJob, { signal } = {}) {
    const res = await fetch(`${BASE}/api/admin/jobs/${id}/events`, {
      headers: { Authorization: this._auth, Accept: 'text/event-stream' },
      signal,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
    if (!res.body) throw new Error('Live job stream is unavailable');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n');
      let boundary = buffer.indexOf('\n\n');
      while (boundary >= 0) {
        const block = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        let event = 'message';
        const data = [];
        block.split('\n').forEach(line => {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          if (line.startsWith('data:')) data.push(line.slice(5).trim());
        });
        if (data.length && event === 'job') onJob(JSON.parse(data.join('\n')));
        if (data.length && event === 'error') {
          const payload = JSON.parse(data.join('\n'));
          throw new Error(payload.detail || 'Job stream failed');
        }
        boundary = buffer.indexOf('\n\n');
      }
      if (done) break;
    }
  }

  async importPlaces(formData) {
    const res = await fetch(BASE + '/api/admin/places/import', {
      method: 'POST',
      headers: { Authorization: this._auth },
      body: formData,
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`${res.status}: ${text || res.statusText}`);
    }
    return res.json();
  }

  // Subscribers
  getSubscribers() { return this._fetch('/api/admin/subscribers'); }
}

const api = new Api();
export default api;
